/* useKeyboardNav — one window keydown listener for the whole survey.
   Reads the latest actions via a ref so the listener (registered once) never
   goes stale. ←/→ move, Enter / Ctrl+Enter continue, 1–9 pick an option. */
import { useEffect, useRef } from 'react';

export interface KeyActions {
  doNext: () => void | Promise<void>;
  back: () => void;
  canGoBack: boolean;
  selectIndex: (i: number) => void;
}

export function useKeyboardNav(actions: KeyActions): void {
  const ref = useRef(actions);
  ref.current = actions;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const c = ref.current;
      if (e.metaKey || e.ctrlKey) {
        if (e.key === 'Enter') {
          e.preventDefault();
          c.doNext();
        }
        return;
      }
      if (e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && t.matches && t.matches('input, textarea, select')) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        c.doNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (c.canGoBack) c.back();
      } else if (e.key === 'Enter') {
        if (document.activeElement && document.activeElement.tagName === 'BUTTON') return;
        e.preventDefault();
        c.doNext();
      } else if (/^[1-9]$/.test(e.key)) {
        c.selectIndex(parseInt(e.key, 10) - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
